import http from "./http";
import APIS_CONSTANTS from "./ApiConstants";

// Posting an event on the shopper's behalf.
//
// The SDK runs on the brand's own page and sees everything the shopper does on
// the card, but it cannot prove who they are: the backend files an event
// against a customer only when the request carries that customer's own token,
// and the token lives here, inside the frame, on our domain. Handing it out to
// the shop's page would put a credential that identifies and authenticates the
// shopper within reach of every other script the brand has installed.
//
// So the SDK sends the envelope in and this sends it on. Same division as the
// card's Add to cart: the side that knows who the shopper is does the part that
// needs to know.

// The backend has one kind of author for a shopper. A guest customer is a
// customer record like any other; whether they have signed in is a fact about
// the person, carried in the payload.
const AUTHOR_IS_A_CUSTOMER = "CUSTOMER";

// A refused event must not pass in silence, and this is the second time that
// has had to be said.
//
// The first was the SDK posting events itself: the answer was thrown away, so
// eight card events shipped, were refused every single time, and looked
// perfectly healthy from the browser. That was fixed. Then moving the posting
// here — so the event could carry the shopper's token — brought the same
// blindness with it, because this side swallowed the failure too. The check has
// to live wherever the posting lives.
//
// Said once per event name per page load: a shopper tapping the same button ten
// times has one problem, not ten, and a brand's console is not ours to flood.
let warned = {};

const warnOnce = (eventName, what) => {
  const key = String(eventName);
  if (warned[key]) return;
  warned[key] = true;
  try {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(`[meetusvr] the event ${key} was not recorded: ${what}`);
    }
  } catch (e) {
    // A console that refuses to be written to changes nothing else here.
  }
};

// Test seam. A page load warns once and that is the point; a test file needs
// each case to start from silence.
export const resetTrackingWarnings = () => {
  warned = {};
};

// The author is taken from our own state and never from the envelope. The
// backend requires the customer number to match the token's, and the SDK's idea
// of who is shopping crosses a frame boundary and can be a moment out of date —
// this side holds both halves, so the two cannot disagree.
export const relayTrackedEvent = (envelope, auth) => {
  if (!envelope || typeof envelope !== "object" || Array.isArray(envelope)) return Promise.resolve();

  const body = { ...envelope };
  delete body.createdById;
  delete body.createdByUserType;

  // Only with a token. Naming a customer the request cannot prove is refused by
  // the backend, and an event saved against nobody is better than one refused
  // outright: the visitor id still carries it, and the identified event that
  // follows joins it up.
  const customerId = auth?.token ? auth?.user?.id : null;
  if (customerId) {
    body.createdById = customerId;
    body.createdByUserType = AUTHOR_IS_A_CUSTOMER;
  }

  // Silent to the shopper, who has no idea an event exists and would only be
  // confused by a toast about one. Not silent to us: see warnOnce.
  return http
    .post(APIS_CONSTANTS.trackingEvents, body, { silentError: true })
    .catch((error) => {
      const status = error?.response?.status;
      // A request that never got an answer is not a fault worth saying: these
      // go out as pages close, and a browser cancelling one is expected. A
      // refusal is an answer, and it means the event was thrown away.
      if (status) warnOnce(body.eventName, `the shop's backend refused it with HTTP ${status}`);
    });
};
